'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import {
  GroupHeaderCell,
  TraceResultBadge,
  TraceStatusBadge,
} from '@terra/ui-design-system';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import type {
  TraceListGrouping,
  TraceListSortableColumn,
} from '../../../store/tab/tracesTabScopedFilterStore';
import { rowClickBlockerProps } from '../rowClickBlocker';
import {
  EXECUTIONS_GROUP_SORTING_ORDER,
  isExecutionsGroupHeaderRow,
  type ExecutionsGridRow,
} from './executions-grouping.model';
import {
  ExecutionActionsCell,
  ExecutionAgentCell,
  ExecutionCreatedAtCell,
  ExecutionObjectiveCell,
} from './ExecutionsTableCells';
import {
  ExecutionsTableSelectAllHeader,
  ExecutionsTableSelectionCell,
} from './ExecutionsTableSelectionCells';

const SELECT_COLUMN_WIDTH = 40;
const ACTIONS_COLUMN_WIDTH = 128;
const preserveServerSortOrder = () => 0;

type ExecutionsDataColumn = {
  colId: TraceListSortableColumn;
  headerName: string;
  flex: number;
  minWidth: number;
  maxWidth?: number;
  render: (trace: TracePreviewDto) => ReactNode;
};

const renderTraceCell =
  (render: (trace: TracePreviewDto) => ReactNode) =>
  (params: ICellRendererParams<ExecutionsGridRow>) =>
    params.data && !isExecutionsGroupHeaderRow(params.data) ? (
      <Box h="full" display="flex" alignItems="center" minW={0}>
        {render(params.data)}
      </Box>
    ) : null;

export function useExecutionsTableColumnDefs({
  grouping,
  collapsedGroupKeys,
  onToggleGroup,
}: {
  grouping: TraceListGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
}): ColDef<ExecutionsGridRow>[] {
  const t = useTranslations('proxy.executions.table');
  const tExecutions = useTranslations('designSystem.executions');
  const groupField = grouping?.field;

  return useMemo<ColDef<ExecutionsGridRow>[]>(() => {
    const columns: ExecutionsDataColumn[] = [
      {
        colId: 'objective',
        headerName: tExecutions('objective'),
        flex: 2,
        minWidth: 160,
        render: (trace) => <ExecutionObjectiveCell trace={trace} />,
      },
      {
        colId: 'agent',
        headerName: tExecutions('filters.agent'),
        flex: 1,
        minWidth: 110,
        maxWidth: 200,
        render: (trace) => <ExecutionAgentCell trace={trace} />,
      },
      {
        colId: 'status',
        headerName: tExecutions('columnStatus'),
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        render: (trace) => <TraceStatusBadge status={trace.status} />,
      },
      {
        colId: 'result',
        headerName: tExecutions('result'),
        flex: 1,
        minWidth: 160,
        maxWidth: 260,
        render: (trace) => <TraceResultBadge trace={trace} />,
      },
      {
        colId: 'createdAt',
        headerName: t('created'),
        flex: 1,
        minWidth: 140,
        maxWidth: 220,
        render: (trace) => <ExecutionCreatedAtCell trace={trace} />,
      },
    ];
    const dataColumns = columns.map<ColDef<ExecutionsGridRow>>(
      (column, index) => ({
        colId: column.colId,
        headerName: column.headerName,
        flex: column.flex,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        sortable: true,
        comparator: preserveServerSortOrder,
        ...(column.colId === groupField && {
          sortingOrder: EXECUTIONS_GROUP_SORTING_ORDER,
        }),
        colSpan: (params) =>
          index === 0 && isExecutionsGroupHeaderRow(params.data)
            ? columns.length
            : 1,
        cellRenderer:
          index === 0
            ? (params: ICellRendererParams<ExecutionsGridRow>) =>
                isExecutionsGroupHeaderRow(params.data) ? (
                  <GroupHeaderCell
                    group={params.data}
                    isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
                    onToggle={onToggleGroup}
                    containerProps={rowClickBlockerProps}
                  />
                ) : (
                  renderTraceCell(column.render)(params)
                )
            : renderTraceCell(column.render),
      }),
    );

    return [
      {
        colId: 'select',
        headerName: '',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponent: ExecutionsTableSelectAllHeader,
        cellRenderer: (params: ICellRendererParams<ExecutionsGridRow>) =>
          params.data && !isExecutionsGroupHeaderRow(params.data) ? (
            <ExecutionsTableSelectionCell trace={params.data} />
          ) : null,
      },
      ...dataColumns,
      {
        colId: 'actions',
        headerName: '',
        width: ACTIONS_COLUMN_WIDTH,
        minWidth: ACTIONS_COLUMN_WIDTH,
        maxWidth: ACTIONS_COLUMN_WIDTH,
        pinned: 'right',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<ExecutionsGridRow>) =>
          params.data && !isExecutionsGroupHeaderRow(params.data) ? (
            <ExecutionActionsCell trace={params.data} />
          ) : null,
      },
    ];
  }, [collapsedGroupKeys, groupField, onToggleGroup, t, tExecutions]);
}
